'use client';

import type { PropsWithChildren } from 'react';
import { useRef, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Checkbox } from '@sola/ark-core/molecules/checkbox/checkbox.tsx';
import { EmailField } from '@sola/ark-core/molecules/email-field/email-field.tsx';
import { Fieldset } from '@sola/ark-core/molecules/fieldset/fieldset.tsx';
import { Form, FormSubmit } from '@sola/ark-core/molecules/form/form.tsx';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.tsx';
import { PostalCodeField } from '@sola/ark-core/molecules/postal-code-field/postal-code-field.tsx';
import { StateField } from '@sola/ark-core/molecules/state-field/state-field.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { generalConfig } from '@sola/config/lib/getters.ts';
import { StateEnum } from '@sola/schema/dotcom/shared/geo.ts';
import { BlockProps } from '@sola/schema/lib/schema.ts';
import type { CompassionSundayBaseFormData } from '@sola/schema/lib/skippy.ts';
import { submitCompassionSundayPlanningGuide } from '@sola/skippy-sdk/requests.ts';
import { trackLeadFormSuccess } from '@sola/tracking/components/datalayer.tsx';

import { NameFields } from '../../../molecules/name-fields/name-fields.tsx';

const FormColumnProps = z.object({
  className: z.string().optional(),
});
type FormColumnProps = z.infer<typeof FormColumnProps> & PropsWithChildren;

const FormState = z.enum(['submitting', 'error', 'success']).optional();
type FormState = z.infer<typeof FormState>;

const FormProps = BlockProps.extend({
  onSubmit: z
    .function({
      input: [z.custom<CompassionSundayBaseFormData>()],
      output: z.promise(z.boolean()),
    })
    .optional(),
  className: z.string().optional(),
});
type FormProps = z.infer<typeof FormProps>;

const FormQueryState = z.object({
  isPastor: z.boolean().optional(),
  isChildHost: z.boolean().optional(),
});
type FormQueryState = z.infer<typeof FormQueryState>;

const stateOptions = Object.entries(StateEnum).map(([abbr, name]) => ({
  text: name,
  value: abbr,
}));

const FormColumn: React.FC<FormColumnProps> = ({ className, children }) => (
  <div
    className={cn('grid sm:grid-cols-2 gap-comfy-sm', {
      [`${className}`]: !!className,
    })}
  >
    {children}
  </div>
);

const AddressFields: React.FC = () => {
  const { t } = useTranslation(['forms', 'skippy']);
  const fieldPrefix = 'shipping';

  const getTranslationKey = (key: string): string => {
    return `forms:addressFields.${key}`;
  };

  return (
    <>
      <InputField
        label={t(getTranslationKey('addressLabel'))}
        name={`${fieldPrefix}Address`}
        autoComplete={`${fieldPrefix} address-line1`}
        required
      />
      <FormColumn>
        <InputField
          label={t(getTranslationKey('cityLabel'))}
          name={`${fieldPrefix}City`}
          autoComplete={`${fieldPrefix} address-level2`}
          required
          maxLength={generalConfig.leadForms.churchCityMaxLength}
        />
        <StateField
          label={t(getTranslationKey('stateLabel'))}
          name={`${fieldPrefix}State`}
          autoComplete={`${fieldPrefix} address-level1`}
          spellCheck={false}
          placeholder={t(getTranslationKey('statePlaceholder'))}
          items={stateOptions}
          required
        />
        <PostalCodeField
          label={t(getTranslationKey('zipCodeLabel'))}
          name={`${fieldPrefix}Postal`}
          autoComplete={`${fieldPrefix} postal-code`}
          required
        />
      </FormColumn>
    </>
  );
};

export const FormCompassionSundayPlanningGuide: React.FC<FormProps> = ({
  className,
  onSubmit = submitCompassionSundayPlanningGuide,
}) => {
  const { t } = useTranslation(['forms', 'skippy']);
  const formRef = useRef<HTMLDivElement>(null);
  const [formState, setFormState] = useState<FormState>();
  const [{ isPastor, isChildHost }, setQueryState] = useState<FormQueryState>(
    {},
  );
  const [acknowledged, setAcknowledged] = useState<boolean>(false);

  const getTranslationKey = (key: string): string => {
    return `skippy:form.compassionSunday.${key}`;
  };

  const handleSubmit = async (data: FieldValues) => {
    // TODO: we may not need this b/c our form validation shouldn't allow us to get here
    if (isChildHost && !acknowledged) {
      return;
    }

    setFormState('submitting');
    try {
      const success = await onSubmit(data as CompassionSundayBaseFormData);
      if (success) {
        trackLeadFormSuccess('CompassionSundayPlanning');
      }
      setFormState(success ? 'success' : 'error');
    } catch {
      setFormState('error');
    }
  };

  return (
    <section
      ref={formRef}
      className={cn('CompassionSundayPlanning component component--no-header', {
        [`${className}`]: !!className,
      })}
    >
      <div className="container">
        {formState === 'success' && (
          <Alert
            className="scroll-mt-roomy-md"
            variant="positive"
            message={t(getTranslationKey('successText'))}
            scrollIntoView
          />
        )}
        {formState === 'error' && (
          <Alert
            className="scroll-mt-roomy-md"
            variant="negative"
            message={t(getTranslationKey('errorText'))}
            scrollIntoView
          />
        )}
        {formState !== 'success' && (
          <Form
            className="CompassionSundayPlanning__form grid grid-col-1 md:items-end gap-comfy-lg md:gap-comfy-sm"
            onSubmit={handleSubmit}
            options={{ mode: 'onTouched' }}
            // aria-label={t(getTranslationKey('ariaLabel'))}
          >
            <div>
              <Fieldset
                id="contact-fields"
                header={t(getTranslationKey('contactHeader'))}
              >
                <FormColumn>
                  <NameFields />
                </FormColumn>
                <EmailField
                  label={t('forms:contactFields.emailLabel')}
                  name="email"
                  required
                />
              </Fieldset>
              <div className="mt-comfy-xl">
                <Text variant={Variants.Label} size={Sizes.XSmall}>
                  {t(getTranslationKey('identifierInfo'))}
                </Text>
                <Checkbox
                  fieldClasses={cn('mt-comfy-md')}
                  label={t(getTranslationKey('pastorLabel'))}
                  name="isPastor"
                  onChange={({ currentTarget }) =>
                    setQueryState((prevState) => ({
                      ...prevState,
                      isPastor: currentTarget.checked,
                    }))
                  }
                />
                <Checkbox
                  fieldClasses={cn('mt-comfy-md')}
                  label={t(getTranslationKey('childLabel'))}
                  name="withKids"
                  onChange={({ currentTarget }) =>
                    setQueryState((prevState) => ({
                      ...prevState,
                      isChildHost: currentTarget.checked,
                    }))
                  }
                />
              </div>
              <Fieldset
                id="shipping-fields"
                header={t(getTranslationKey('shippingHeader'))}
              >
                <AddressFields />
              </Fieldset>
              <Checkbox
                fieldClasses={cn('mt-comfy-xl', { hidden: !isChildHost })}
                label={t(getTranslationKey('acknowledgeLabel'))}
                name="acknowledged"
                required={isChildHost}
                onChange={({ currentTarget }) =>
                  setAcknowledged(currentTarget.checked)
                }
              />
              <FormSubmit
                className="CompassionSundayPlanning__button mt-comfy-lg"
                isBusy={formState === 'submitting'}
                fullWidth={false}
                disabled={isChildHost && !acknowledged}
                // TODO: do we want this disabled until filled?
                disableUntilFilled
              >
                {t(getTranslationKey('planningSubmit'))}
              </FormSubmit>
            </div>
          </Form>
        )}
      </div>
    </section>
  );
};
